import { defineStore } from 'pinia';
import NavItem from '@/definitions/interfaces/NavItem.i';
import ServiceArea from '@/definitions/interfaces/ServiceArea.i';
import FullService from '@/definitions/interfaces/FullService.i';
import { ServiceSubmission } from '@/definitions/interfaces/ServiceSubmission.i';
import ServiceInformation from '@/definitions/interfaces/ServiceInformation.i';
import DataTablePaginationOptions from '@/definitions/interfaces/DataTableSortOptions.i';
import User from '@/definitions/interfaces/User.i';
import Debug from '@/utils/Debug';
import OperatorServiceCache from '@/definitions/interfaces/OperatorServiceCache.i';
import { Filters } from '@/views/operators/logisticsHub/types';
import { ServerOptions } from 'vue3-easy-data-table';

export interface SubstituteSearchFilters {
  applicationsFromDate: string | null;
  applicationsToDate: string | null;
  applicationsFromText: string | null;
  applicationsToText: string | null;
  assignmentsFromDate: string | null;
  assignmentsToDate: string | null;
  assignmentsFromText: string | null;
  assignmentsToText: string | null;
}

export interface OperatorDataState {
  logisticsHubViewCache: {
    filters: Filters | null;
    cache: boolean;
    serverOptions: ServerOptions | null;
  };
  servicesViewCache: OperatorServiceCache;
  servicesCache: FullService[];
  servicesPaginationCache: DataTablePaginationOptions | null;
  servicesFilterCache: object | null;
  allInclusiveServicesSortCache: DataTablePaginationOptions | null;
  tieredServicesSortCache: DataTablePaginationOptions | null;
  serviceSubmissionsSortCache: DataTablePaginationOptions | null;
  servicesTieredCache: FullService[];
  servicesAllInclusiveCache: FullService[];
  serviceSubmissionsCache: ServiceSubmission[];
  serviceAreasCache: ServiceArea[];
  serviceAreasSortCache: DataTablePaginationOptions | null;
  usersCache: User[];
  usersPaginationCache: DataTablePaginationOptions | null;
  usersFilterCache: object | null;
  fullUsersCache: User[];
  fullServicesCache: FullService[];
  fullServiceInformationCache: { [p: string]: ServiceInformation }[];
  insurancesCache: { [p: string]: any }[];
  operatorNavItems: NavItem[];
  specializationsCache: string[];
  accountingServicesCache: FullService[];
  accountingServicesStartCache: FullService[];
  accountingServicesPriorityCache: FullService[];
  reportingServicesCache: FullService[];
  reportingServicesSortCache: DataTablePaginationOptions | null;
  reportingServicesFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  reportingServicesPaginationCache: DataTablePaginationOptions | null;
  reportingUsersCache: User[];
  reportingUsersSortCache: DataTablePaginationOptions | null;
  reportingUsersFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  reportingUsersPaginationCache: DataTablePaginationOptions | null;
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  substituteSearchFiltersCache: { [userId: string]: SubstituteSearchFilters };
}

const useOperatorDataStore = defineStore('OperatorData', {
  /**
    Define defaults
  */
  state: (): OperatorDataState => ({
    logisticsHubViewCache: {
      filters: null,
      cache: true,
      serverOptions: null,
    },
    servicesViewCache: {
      operatorServicesView: '',
      start: '',
      calendarView: '',
    },
    servicesCache: [],
    servicesPaginationCache: null,
    servicesFilterCache: null,
    allInclusiveServicesSortCache: null,
    tieredServicesSortCache: null,
    serviceSubmissionsSortCache: null,
    servicesTieredCache: [],
    servicesAllInclusiveCache: [],
    serviceSubmissionsCache: [],
    serviceAreasCache: [],
    serviceAreasSortCache: null,
    usersCache: [],
    usersPaginationCache: null,
    usersFilterCache: null,
    fullUsersCache: [],
    fullServicesCache: [],
    fullServiceInformationCache: [],
    insurancesCache: [],
    operatorNavItems: [],
    specializationsCache: [],
    accountingServicesCache: [],
    accountingServicesStartCache: [],
    accountingServicesPriorityCache: [],
    reportingServicesCache: [],
    reportingServicesSortCache: null,
    reportingServicesFiltersCache: null,
    reportingServicesPaginationCache: null,
    reportingUsersCache: [],
    reportingUsersSortCache: null,
    reportingUsersFiltersCache: null,
    reportingUsersPaginationCache: null,
    isImpersonating: false,
    impersonatedUserId: null,
    substituteSearchFiltersCache: {},
  }),
  actions: {
    startImpersonation(userId: string) {
      this.isImpersonating = true;
      this.impersonatedUserId = userId;
    },
    stopImpersonation() {
      this.isImpersonating = false;
      this.impersonatedUserId = null;
    },
    /**
     * reset partner store
     * @return {void}
     */
    resetServicesViewCache(): void {
      this.servicesViewCache = {
        operatorServicesView: '',
        start: '',
        calendarView: '',
      };
    },
    /**
     * reset partner store
     * @return {void}
     */
    resetOperatorStore(): void {
      this.servicesViewCache = {
        operatorServicesView: '',
        start: '',
        calendarView: '',
      };
      this.servicesCache = [];
      this.servicesPaginationCache = null;
      this.allInclusiveServicesSortCache = null;
      this.tieredServicesSortCache = null;
      this.serviceSubmissionsSortCache = null;
      this.servicesTieredCache = [];
      this.servicesAllInclusiveCache = [];
      this.serviceSubmissionsCache = [];
      this.serviceAreasCache = [];
      this.serviceAreasSortCache = null;
      this.usersCache = [];
      this.usersPaginationCache = null;
      this.fullUsersCache = [];
      this.fullServicesCache = [];
      this.fullServiceInformationCache = [];
      this.insurancesCache = [];
      this.operatorNavItems = [];
      this.specializationsCache = [];
      this.accountingServicesCache = [];
      this.accountingServicesStartCache = [];
      this.accountingServicesPriorityCache = [];
      this.reportingServicesCache = [];
      this.reportingServicesSortCache = null;
      this.reportingServicesFiltersCache = null;
      this.reportingServicesPaginationCache = null;
      this.reportingUsersCache = [];
      this.reportingUsersSortCache = null;
      this.reportingUsersFiltersCache = null;
      this.reportingUsersPaginationCache = null;
      this.isImpersonating = false;
      this.impersonatedUserId = null;
      this.substituteSearchFiltersCache = {};
    },
  },
  persist: {
    key: 'arztpool24.de-operators',
    debug: Debug.isEnabled(),
  },
});

export default useOperatorDataStore;
